import { CustomerExtractor } from '@/modules/conversion/commerce/CustomerExtractor';

import type { CustomerIdentityService } from './CustomerIdentityService';

const SCAN_DEBOUNCE_MS = 200;

/**
 * Observa input/change no document, extrai customer dos forms e atualiza o store.
 */
export class CustomerIdentityObserver {
  private readonly identity: CustomerIdentityService;

  private readonly extractor: CustomerExtractor;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private started = false;

  private readonly onFormEvent = (): void => {
    this.scheduleScan();
  };

  constructor(
    identity: CustomerIdentityService,
    extractor?: CustomerExtractor
  ) {
    this.identity = identity;
    this.extractor = extractor ?? new CustomerExtractor();
  }

  start(): void {
    if (this.started || typeof document === 'undefined') return;
    this.started = true;
    this.scan();
    document.addEventListener('input', this.onFormEvent, true);
    document.addEventListener('change', this.onFormEvent, true);
  }

  destroy(): void {
    if (!this.started) return;
    this.started = false;
    if (typeof document !== 'undefined') {
      document.removeEventListener('input', this.onFormEvent, true);
      document.removeEventListener('change', this.onFormEvent, true);
    }
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  scan(): void {
    if (typeof document === 'undefined') return;

    const forms = document.forms;
    for (let i = 0; i < forms.length; i += 1) {
      this.identity.update(this.extractor.extractFromForm(forms[i] ?? null));
    }

    if (document.body) {
      this.identity.update(this.extractor.extractFromContainer(document.body));
    }
  }

  private scheduleScan(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.scan();
    }, SCAN_DEBOUNCE_MS);
  }
}
